// Module-level cache for the demo's GeoJSON sources. Keeps the network quiet
// across remounts and avoids re-parsing 8+ MB payloads when the basemap style
// reloads. Lookups by `cadastre_polygon_pid` for the parcel detail panel are
// indexed lazily on first read so they stay O(1) on subsequent clicks.

import { LAYER_SOURCES } from "../config/agricultural-lulc";
import type { LayerFeatureData } from "../types/agricultural-lulc";

// Resolved data, populated when a fetch completes.
const cache = new Map<string, GeoJSON.FeatureCollection>();
// Promise of an in-flight fetch — second/third callers join this rather than
// firing duplicate requests.
const pending = new Map<string, Promise<GeoJSON.FeatureCollection>>();
// Per-FC index keyed by `cadastre_polygon_pid`, built lazily.
const indexByFc = new WeakMap<
  GeoJSON.FeatureCollection,
  Map<string, GeoJSON.Feature>
>();

// Re-used as placeholder data for sources that register before their real
// FeatureCollection has finished downloading. Mapbox needs *some* GeoJSON to
// register a layer; we swap it out via `setData` once the fetch resolves.
export const EMPTY_FC: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };

export function fetchSource(url: string): Promise<GeoJSON.FeatureCollection> {
  if (url === "EMPTY") return Promise.resolve(EMPTY_FC);
  const hit = cache.get(url);
  if (hit) return Promise.resolve(hit);
  const inflight = pending.get(url);
  if (inflight) return inflight;
  const p = fetch(url)
    .then((res) => res.json())
    .then((data: GeoJSON.FeatureCollection) => {
      cache.set(url, data);
      pending.delete(url);
      return data;
    });
  pending.set(url, p);
  return p;
}

export function getCachedFc(url: string): GeoJSON.FeatureCollection | undefined {
  return cache.get(url);
}

// Index a FeatureCollection by `cadastre_polygon_pid` once, then reuse. Used
// by every parcel-click attribution lookup — the FCs hold ~6,200 features and
// scanning them linearly per click adds up.
function indexFor(fc: GeoJSON.FeatureCollection): Map<string, GeoJSON.Feature> {
  let idx = indexByFc.get(fc);
  if (!idx) {
    idx = new Map();
    for (const f of fc.features) {
      const pid = f.properties?.cadastre_polygon_pid;
      if (typeof pid === "string") idx.set(pid, f);
    }
    indexByFc.set(fc, idx);
  }
  return idx;
}

export function getFeatureByPid(
  url: string,
  pid: string
): GeoJSON.Feature | undefined {
  const fc = cache.get(url);
  return fc ? indexFor(fc).get(pid) : undefined;
}

// Pull the per-parcel `breakdown` JSON column from each layer's cached FC.
// The breakdowns are pre-aggregated into the GeoJSON feature properties at
// build time.
export function queryLayerDataForParcel(pid: string): LayerFeatureData {
  const breakdown = (url: string): string | undefined => {
    const fc = cache.get(url);
    if (!fc) return undefined;
    const feature = indexFor(fc).get(pid);
    return feature?.properties?.breakdown as string | undefined;
  };

  return {
    lulc2024Breakdown: breakdown(LAYER_SOURCES.lulc2024),
    lulc2025Breakdown: breakdown(LAYER_SOURCES.lulc2025),
    cropsBreakdown: breakdown(LAYER_SOURCES.crops2025),
    forestBreakdown: breakdown(LAYER_SOURCES.forest2025),
  };
}
